/**
 * Further north (ROUND7 §5 stations 4 and 6-8; the dog, station 5, is `stepsDog.ts`): past the watcher by the
 * trench, the range, the practice wall, and the way out - Recall and the ladder. The numbers in the notes are the
 * sim's own (`tuning.ts`), so the course never teaches a figure the game does not use.
 */

import { COURSE, COURSE_IDS } from '../../gen/training';
import { BOMB, GRENADE, LAW, OSPREY } from '../../sim/tuning';
import { TICK_RATE, type World } from '../../sim/types';
import { onGround, type Step } from './stepTypes';

const seconds = (ticks: number): number => Math.round(ticks / TICK_RATE);

/** Further than this from the X, a walker cannot make the ladder before the Osprey leaves again. */
const FAR_FROM_X = 80;

const standing = (w: World, id: number): boolean => w.props.some((p) => p.id === id && !p.destroyed);

/** Over the stream (ROUND8 §5): the dog station is behind him. */
const pastStream = (w: World): boolean => w.ranger.z < COURSE.dogs.stream.north;

/** Over the stream and short of the wall: the range. */
const atRange = (w: World): boolean => onGround(w) && pastStream(w) && w.ranger.z > COURSE.wall.z + 6;

const trench: Step = {
  id: 'trench', station: 'BEING SEEN', title: 'PAST THE WATCHER',
  text: ({ keys }) => `THE WATCHER AHEAD FACES YOUR WAY. GET INTO THE TRENCH TO THE EAST AND CRAWL `
    + `${keys.key('crawl')} ALONG IT, PAST HIM.`,
  note: () => 'CRAWLING IN A TRENCH, NOBODY OUTSIDE IT CAN SEE YOU. IF A MAN SEES YOU, AN ARC AROUND YOU FILLS: '
    + 'FULL MEANS SEEN. YOUR GHILLIE WRAP SLOWS IT WHILE YOU ARE LOW.',
  // Out past the end of the trench the watcher is behind him: the dog team's beat starts a few metres on (ROUND8 §5),
  // and "the watcher ahead" would be wrong there.
  ready: ({ world }) => onGround(world) && world.ranger.z > COURSE.trench.z0,
  done: ({ facts }) => facts.crawledPast,
};

const carbine: Step = {
  id: 'carbine', station: 'THE RANGE', title: 'THE CARBINE',
  text: ({ keys }) => `THE RANGE: TAKE THE CARBINE ${keys.key('weapon1')}, AIM AT A LAMP ON THE POLES TO THE WEST `
    + `AND FIRE ${keys.key('fire')}.`,
  note: ({ keys }) => `${keys.key('reload')} RELOADS. ONE ROUND PUTS A SEARCHLIGHT OUT FOR GOOD - `
    + 'AT NIGHT, THAT MATTERS.',
  ready: ({ world }) => atRange(world) && COURSE_IDS.lamps.some((id) => standing(world, id)),
  done: ({ facts }) => facts.lampOut,
};

const grenade: Step = {
  id: 'grenade', station: 'THE RANGE', title: 'THE GRENADE',
  text: ({ keys }) => (keys.classic
    ? `GRENADES ${keys.key('weapon2')}: FACE THE CRATES TO THE EAST AND THROW ${keys.key('use')}.`
    : `GRENADES ${keys.key('weapon2')}: PUT THE CURSOR ON THE CRATES TO THE EAST - IT SETS HOW FAR - `
      + `AND THROW ${keys.key('use')}.`),
  note: () => `${GRENADE.maxRange} M AT MOST. A GRENADE HURTS ANYONE WITHIN ${GRENADE.radius} M OF IT - YOU TOO.`,
  ready: ({ world }) => atRange(world) && world.ranger.inventory.grenades > 0
    && COURSE_IDS.crates.some((id) => standing(world, id)),
  done: ({ facts }) => facts.crateBlown,
};

/**
 * How far back to stand for the LAW. It arms after `LAW.armDistance` of FLIGHT, but it leaves the tube 0.8 m ahead
 * of him and strikes a wall its own width short of the face: 6 m back was a dud - found by playing the course
 * through real inputs (2026-09-23). Two metres more is always armed.
 */
const LAW_STAND_OFF = LAW.armDistance + 2;

const wall: Step = {
  id: 'wall', station: 'THE WALL', title: 'A GAP IN THE WALL',
  text: ({ keys }) => `BLOW A GAP IN THE MIDDLE OF THE WALL: THE LAW ${keys.key('weapon3')} FROM ${LAW_STAND_OFF} M `
    + `BACK OR MORE, OR A TIME BOMB ${keys.key('weapon4')} SET AGAINST IT.`,
  note: ({ keys }) => `GET ${BOMB.radius} M CLEAR OF A BOMB. ${keys.key('fuse5')} ${keys.key('fuse10')} `
    + `${keys.key('fuse15')} SET ITS FUSE. A GRENADE DOES NOTHING TO A WALL.`,
  ready: ({ world }) => onGround(world) && pastStream(world) && standing(world, COURSE_IDS.wallTarget)
    && (world.ranger.inventory.laws > 0 || world.ranger.inventory.bombs > 0),
  done: ({ facts }) => facts.wallBreached,
};

const recall: Step = {
  id: 'recall', station: 'THE WAY OUT', title: 'RECALL',
  text: ({ keys }) => `HOLD ${keys.key('recall')} TO RECALL THE OSPREY. IT TAKES `
    + `${seconds(OSPREY.inboundTicks)} SECONDS TO COME TO THE X.`,
  note: ({ world }) => {
    const r = world.ranger;
    const away = Math.round(Math.sqrt((r.x - COURSE.pickup.x) ** 2 + (r.z - COURSE.pickup.z) ** 2));
    return away > FAR_FROM_X
      ? `THE X IS ${away} M NORTH. CALL IT WHEN YOU CAN BE THERE IN TIME: `
        + `IT WAITS ONLY ${seconds(OSPREY.hoverTicks)} S.`
      : 'RECALL IS THE ONLY WAY OUT OF A MISSION. THE X IS JUST NORTH OF THE WALL.';
  },
  ready: ({ world }) => onGround(world) && world.osprey.phase === 'away',
  done: ({ facts }) => facts.recalled,
};

const ladder: Step = {
  id: 'ladder', station: 'THE WAY OUT', title: 'ABOARD',
  text: ({ world }) => (world.osprey.phase === 'hovering'
    ? 'IT IS OVER THE X: STAND UNDER THE LADDER UNTIL YOU ARE ABOARD.'
    : 'THE OSPREY IS ON ITS WAY TO THE X, NORTH OF THE WALL: BE THERE WHEN IT ARRIVES.'),
  note: () => `IT WAITS ${seconds(OSPREY.hoverTicks)} SECONDS. MISS IT AND YOU ARE ON YOUR OWN.`,
  ready: ({ world }) => onGround(world) && (world.osprey.phase === 'inbound' || world.osprey.phase === 'hovering'),
  done: ({ facts }) => facts.boarded,
};

/** Station 4, before the dog. */
export const SEEN_STEPS: readonly Step[] = [trench];
/** Stations 6-8, after it. */
export const FIELD_STEPS: readonly Step[] = [carbine, grenade, wall, recall, ladder];
